import { useEffect } from 'react'
import { useBlocker } from '@tanstack/react-router'

/**
 * Warns the user when they try to navigate away or close the tab with unsaved
 * changes. Uses TanStack Router's `useBlocker` for in-app navigation and
 * `window.onbeforeunload` for browser tab close / refresh.
 *
 * @param isDirty — true when the form has unsaved changes
 * @param message — custom confirmation message (default: standard unsaved-changes text)
 *
 * @example
 *   const [dirty, setDirty] = useState(false)
 *   useUnsavedChangesWarning(dirty)
 */
export function useUnsavedChangesWarning(
  isDirty: boolean,
  message = 'You have unsaved changes. Are you sure you want to leave?',
) {
  // ── In-app navigation guard (sidebar clicks, route changes) ──────────
  const blocker = useBlocker({
    condition: isDirty,
  })

  useEffect(() => {
    if (blocker.status === 'blocked') {
      const proceed = window.confirm(message)
      if (proceed) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker.status, message])

  // ── Browser tab close / refresh guard ────────────────────────────────
  useEffect(() => {
    if (!isDirty) return

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Modern browsers ignore custom messages for security reasons,
      // but setting returnValue triggers the native "Leave site?" dialog.
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])
}
